import React, { useCallback } from 'react';
import { useIntl } from 'react-intl';
import { HeaderProps } from './types';
import {
  StyledBreadcrumb,
  StyledCmdKHint,
  StyledHeader,
  StyledHeaderAction,
  StyledHeaderMobileToggle,
  StyledHeaderSpacer,
  StyledNotificationBell,
} from './styles';
import useHeaderTitleContext from '../../../hooks/useHeaderTitleContext';
import MenuDropdown from '../../molecules/MenuDropdown';
import Seo from '../../Seo';
import { IconChevronRight, IconClose, IconSearch } from '../../../assets/svg/icons';
import { MARKETPLACE_TEST_BOOT } from '../../layouts/MainLayout/consts';
import { START_TOUR_EVENT } from '../FeatureTour/consts';
import { StyledHelpButton } from '../FeatureTour/styles';

const openPalette = () =>
  window.dispatchEvent(
    new KeyboardEvent('keydown', { metaKey: true, key: 'k', bubbles: true })
  );

const IconMenu = () => (
  <svg
    viewBox='0 0 24 24'
    fill='none'
    stroke='currentColor'
    strokeWidth={2}
    strokeLinecap='round'
  >
    <line x1='3' y1='6' x2='21' y2='6' />
    <line x1='3' y1='12' x2='21' y2='12' />
    <line x1='3' y1='18' x2='21' y2='18' />
  </svg>
);

const HelpIcon = () => (
  <svg viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth={1.75} strokeLinecap='round' strokeLinejoin='round'>
    <circle cx='12' cy='12' r='10' />
    <path d='M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3' />
    <line x1='12' y1='17' x2='12.01' y2='17' />
  </svg>
);

const BellIcon = () => (
  <svg viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth={1.75} strokeLinecap='round' strokeLinejoin='round'>
    <path d='M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9' />
    <path d='M13.73 21a2 2 0 0 1-3.46 0' />
  </svg>
);

const Header: React.FC<HeaderProps> = ({
  menu,
  centerComponent,
  onMenuToggle,
  mobileMenuOpen,
  workspaceName,
  userInitials,
}) => {
  const intl = useIntl();
  const { title } = useHeaderTitleContext();
  const handleCmdK = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    openPalette();
  }, []);

  return (
    <>
      <Seo {...{ title }} />
      <StyledHeader>
        {onMenuToggle && (
          <StyledHeaderMobileToggle
            type='button'
            onClick={onMenuToggle}
            aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
          >
            {mobileMenuOpen ? <IconClose /> : <IconMenu />}
          </StyledHeaderMobileToggle>
        )}

        {/* Breadcrumb */}
        <StyledBreadcrumb>
          {workspaceName && (
            <>
              <span className='ws-name'>{workspaceName}</span>
              <span className='chev'><IconChevronRight /></span>
            </>
          )}
          {title && <span className='page-title'>{title}</span>}
        </StyledBreadcrumb>

        <StyledHeaderSpacer />
        {centerComponent}

        <StyledHeaderAction>
          {/* Full search bar / ⌘K trigger — hidden under test-boot flag */}
          {!MARKETPLACE_TEST_BOOT && (
            <StyledCmdKHint
              type='button'
              onClick={handleCmdK}
              title='Search & navigate (⌘K)'
            >
              <span className='search-icon'><IconSearch /></span>
              <span className='placeholder'>
                {intl.formatMessage({
                  id: 'search.globalPlaceholder',
                  defaultMessage: 'Search orders, customers, products…',
                })}
              </span>
              <kbd>⌘K</kbd>
            </StyledCmdKHint>
          )}

          {/* Replay the what's-new feature tour */}
          <StyledHelpButton
            type='button'
            aria-label={intl.formatMessage({
              id: 'tour.helpButton',
              defaultMessage: "What's new tour",
            })}
            title={intl.formatMessage({
              id: 'tour.helpButton',
              defaultMessage: "What's new tour",
            })}
            onClick={() => window.dispatchEvent(new CustomEvent(START_TOUR_EVENT))}
          >
            <HelpIcon />
          </StyledHelpButton>

          {/* User avatar / account dropdown */}
          {menu && (
            <MenuDropdown {...menu} userInitials={userInitials} />
          )}
        </StyledHeaderAction>
      </StyledHeader>
    </>
  );
};

export default Header;
